import {
  BootstrapResponse,
  PUBLIC_DOC_CATEGORY_SLUGS,
} from '@listening/api-contract';
import { ThemeProvider } from '@/components/shared-ui/theme-provider';
import { DefaultCatchBoundary } from '@/components/start/DefaultCatchBoundary';
import { Providers } from '@/components/web-fe/misc/providers/providers';
import { isSSR } from '@/env';

import type { Iso3166_1_alpha_2_partial } from '@/lib/utils';
import { seo } from '@/seo/seo';
import { cookieTrackersSiteWide } from '@/seo/cookie-trackers-site-wide';
import { initPostHogFromRouteContext } from '@/services/posthog-init';
import { redirectServerFn } from '@/server/redirects';
import { callApiPromise } from '@/runtimes/get-runtime';
import {
  cloudfrontRegionQueryOptions,
  getCloudfrontRegionFn,
} from '@/hooks/use-free-plan-availability';
import appCss from '@/styles/app.css?url';

import '@fontsource-variable/atkinson-hyperlegible-next';
import '@fontsource-variable/inter';
import '@fontsource-variable/lexend';
import '@fontsource-variable/plus-jakarta-sans';
import '@fontsource/opendyslexic/400.css';
import '@fontsource/opendyslexic/700.css';
import '@fontsource/poppins/100.css';
import '@fontsource/poppins/200.css';
import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/800.css';
import '@fontsource/poppins/900.css';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
} from '@tanstack/react-router';
import { getLocale } from '@/paraglide/runtime';
// cdn-shared responses must NOT bake per-user state (distinctId from
// Analytics.Bootstrap) into the HTML or it leaks across users sharing a
// CDN entry. For those routes we instead resolve the edge-bucketed variant
// assignments from `x-variants` (or the SSR-lambda fallback) and put
// them into route context so `useFlag` returns the right variant on first
// paint without any per-user identity in the cached HTML.
//
// `resolveSsrAnalyticsContext` and `emitPostHogCookieIfNew` are wrapped with
// `createServerOnlyFn` in their source files. The TanStack Start compiler
// plugin replaces the bodies with throw-stubs in client builds, and the
// import-protection plugin recognizes `createServerOnlyFn` as a safe
// boundary for `@tanstack/react-start/server` usages — so the inner
// closures and their `setCookie` dependencies tree-shake out cleanly.
// (Files deliberately do NOT use `*.server.ts` naming; that pattern is in
// the plugin's file-deny list and would auto-mock the imports here.)
import { resolveSsrAnalyticsContext } from '@/analytics/ssr-variant-resolver';
import { resolveEdgeFlagsClient } from '@/analytics/client-variant-resolver';
import { emitPostHogCookieIfNew } from '@/analytics/posthog-cookie';
import {
  applyEdgeFlags,
  EdgeFlagsProvider,
  type EdgeFlags,
} from '@listening/frontend/analytics/client';
import { rootLogger } from '@/lib/root-logger';

const moduleLogger = rootLogger.child({ module: '__root' });

type AnalyticsBootstrapData = typeof BootstrapResponse.Type;

const publicDocCategorySlugs = new Set<string>(PUBLIC_DOC_CATEGORY_SLUGS);

/**
 * Read the root route's stored `__beforeLoadContext` out of TanStack's match
 * array, to carry the SSR-computed `cloudfrontRegion` / `analyticsBootstrap`
 * forward across a client-side nav (consumed elsewhere — e.g.
 * `use-free-plan-availability` reads `cloudfrontRegion`). `edgeFlags` is
 * intentionally not modelled here — the client branch recomputes it per nav.
 *
 * `matches` is typed `unknown` deliberately — that one decision does two jobs:
 *   1. lets the cast below be a SINGLE `as`, not `as unknown as` (casting
 *      *from* `unknown` is always one step), and
 *   2. avoids reading a TYPED property off the `RouteMatch` union — INCLUDING
 *      `routeId` — which collapses whole-app route-tree inference per §5 of
 *      `docs/projects/active/a-b-test-public-doc.md`. Laundering through
 *      `unknown` erases that deep union, so `.find` is a plain structural read
 *      (also why we don't depend on root being `matches[0]`).
 */
function readRootBeforeLoadContext(matches: unknown) {
  const structuralMatches = matches as ReadonlyArray<{
    routeId?: string;
    __beforeLoadContext?: {
      cloudfrontRegion: Iso3166_1_alpha_2_partial | undefined;
      isFreePlanCountry: boolean | undefined;
      analyticsBootstrap: AnalyticsBootstrapData | undefined;
    };
  }>;
  return structuralMatches.find((m) => m.routeId === '__root__')
    ?.__beforeLoadContext;
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  cloudfrontRegion: Iso3166_1_alpha_2_partial | undefined;
  /**
   * Free-plan geography bit, SSR-resolved only on shared-cached paths keyed
   * by `x-free-plan-country` (where `cloudfrontRegion` stays undefined so no
   * per-viewer country bakes into the shared entry). `useFreePlan` prefers
   * this and falls back to `isFreePlanCountryCode(cloudfrontRegion)`.
   */
  isFreePlanCountry: boolean | undefined;
  analyticsBootstrap: AnalyticsBootstrapData | undefined;
  edgeFlags: EdgeFlags | undefined;
}>()({
  server: {},
  beforeLoad: async (ctx) => {
    if (isSSR) {
      await redirectServerFn({ data: { pathname: ctx.location.pathname } });

      // Category-page disambiguation (`/doc/<category-slug>` vs `/doc/<docId>`)
      // lives inside `resolveSsrAnalyticsContext` now — it consults the
      // `excludePattern` on each `experimentalRoutes` entry. Excluded paths
      // come back as a non-enrolled `EdgeFlags` map (not `undefined`) so
      // `applyEdgeFlags`'s cleanup paths still run for users with stale
      // super-properties from prior sessions.
      const { isSharedCached, isFreePlanCountry, edgeFlags } =
        resolveSsrAnalyticsContext(ctx.location.pathname);

      // For cdn-shared routes: skip Analytics.Bootstrap entirely because
      // cdn-shared HTML must NOT carry a per-user distinctId. Edge flags
      // are already resolved synchronously above. For private routes:
      // existing Analytics.Bootstrap flow.
      const [cloudfrontRegion, analyticsBootstrap] = await Promise.all([
        // The viewer's country never bakes into cdn-shared HTML — a shared
        // entry is keyed (at most) by the 1-bit `x-free-plan-country`, and
        // `isFreePlanCountry` above (resolved from that same stamped bit) is
        // the only country-derived value a shared-cached render may use.
        // Baking the full region would publish the SEEDING viewer's country
        // to everyone on that entry. The client branch below prefetches it
        // after hydration instead.
        isSharedCached ? Promise.resolve(undefined) : getCloudfrontRegionFn(),
        isSharedCached
          ? Promise.resolve(undefined)
          : callApiPromise((api) => api.Analytics.Bootstrap(), {
              signal: ctx.abortController.signal,
            }).catch((error: unknown) => {
              // SSR analytics bootstrap is best-effort — log so the silence is
              // visible, but don't block the page render on it.
              moduleLogger.warn(
                { err: error },
                'SSR Analytics.Bootstrap failed',
              );
              return undefined;
            }),
      ]);

      // WS2 task 15: when Analytics.Bootstrap mints a fresh distinctId
      // (anonymous user without a PostHog cookie), persist it on the
      // browser response so subsequent page loads keep the same identity.
      emitPostHogCookieIfNew(analyticsBootstrap);

      return {
        cloudfrontRegion,
        isFreePlanCountry,
        analyticsBootstrap,
        edgeFlags,
      };
    }

    // Client branch: TanStack runs root `beforeLoad` on every SPA nav
    // (verified in `docs/cloned-repos-as-docs/router/packages/router-core/
    // src/load-matches.ts:517`). Recompute `edgeFlags` from the
    // `lis_bucket` cookie + the same bucketing primitives the edge function
    // uses, and fire PostHog override + exposure event via `applyEdgeFlags`.
    //
    // Without this, users who first land on a non-experimental route (e.g.
    // `/app/...`) and SPA-navigate to `/doc/X` keep the original load's
    // `edgeFlags: undefined` in route context — `useFlag` falls back to
    // `default = 'control'` regardless of edge bucket, and no exposure
    // event fires.
    //
    // `priorRootContext` carries the SSR-computed `cloudfrontRegion` /
    // `analyticsBootstrap` forward across this nav — see
    // `readRootBeforeLoadContext`.
    const edgeFlags = resolveEdgeFlagsClient(ctx.location.pathname);
    if (edgeFlags) applyEdgeFlags(edgeFlags);

    const priorRootContext = readRootBeforeLoadContext(ctx.matches);
    if (priorRootContext?.cloudfrontRegion === undefined) {
      void ctx.context.queryClient
        .query(cloudfrontRegionQueryOptions)
        .catch(() => undefined);
    }

    return {
      cloudfrontRegion: priorRootContext?.cloudfrontRegion,
      isFreePlanCountry: priorRootContext?.isFreePlanCountry,
      analyticsBootstrap: priorRootContext?.analyticsBootstrap,
      edgeFlags,
    };
  },
  errorComponent: (props) => <DefaultCatchBoundary {...props} />,
  component: RootComponent,
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      ...seo({
        title: 'Listening App',
      }),
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      // { rel: 'manifest', href: '/site.webmanifest', color: '#fffff' },
      // {
      //   rel: 'apple-touch-icon',
      //   sizes: '180x180',
      //   href: '/apple-touch-icon.png',
      // },
      // {
      //   rel: 'icon',
      //   type: 'image/png',
      //   sizes: '32x32',
      //   href: '/favicon-32x32.png',
      // },
      // {
      //   rel: 'icon',
      //   type: 'image/png',
      //   sizes: '16x16',
      //   href: '/favicon-16x16.png',
      // },
      { rel: 'icon', type: 'image/svg+xml', href: '/favicon.ico?v=2' },
    ],
    scripts: cookieTrackersSiteWide(),
  }),
});

function RootComponent() {
  const { analyticsBootstrap, edgeFlags } = Route.useRouteContext({
    select: (ctx) => ({
      analyticsBootstrap: ctx.analyticsBootstrap,
      edgeFlags: ctx.edgeFlags,
    }),
  });

  // Init PostHog with bootstrap + edge-resolved variants from route context
  // (server function data dehydrated by Start). Runs once during render,
  // before children mount, so flags are synchronous.
  initPostHogFromRouteContext(analyticsBootstrap, edgeFlags);

  const matches = useRouterState({ select: (state) => state.matches });

  const isAppRoute =
    !matches.some((match) => match.status === 'notFound') &&
    matches.some(({ routeId, params }) => {
      if (routeId.startsWith('/app')) return true;
      if (routeId.startsWith('/_marketing/public-tools/readalong')) {
        return true;
      }
      if (!routeId.startsWith('/_marketing/doc/$docId')) return false;

      const docId = 'docId' in params ? params.docId : undefined;
      return !docId || !publicDocCategorySlugs.has(docId);
    });

  return (
    <RootDocumentInternal isAppRoute={isAppRoute}>
      <EdgeFlagsProvider edgeFlags={edgeFlags}>
        <ThemeProvider {...(isAppRoute ? {} : { forcedTheme: 'light' })}>
          <Providers>
            <Outlet />
          </Providers>
        </ThemeProvider>
      </EdgeFlagsProvider>
    </RootDocumentInternal>
  );
}

function RootDocumentInternal({
  children,
  isAppRoute,
}: {
  children: React.ReactNode;
  isAppRoute: boolean;
}) {
  const htmlClassName = isAppRoute
    ? 'h-screen w-full subpixel-antialiased'
    : '';
  const bodyClassName = isAppRoute
    ? 'size-full overflow-x-hidden'
    : 'overflow-visible text-[16px] font-[400] leading-[26px] lg:text-[20px]';

  return (
    <html className={htmlClassName} lang={getLocale()} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body className={bodyClassName}>
        {children}
        <Scripts />
      </body>
    </html>
  );
}
